import * as Yup from 'yup';
import { ISelect } from '../../../../shared/types/shared';
import { normalizeText } from '../../../../shared/utils/strings';

export enum OrbitChannelEnum {
    PRINCIPAL = 'principal',
    TELEFONICO = 'telefonico',
    WHATSAPP = 'whatsapp',
    BACKOFFICE = 'backoffice',
    CHAT = 'chat',
    VIDEO = 'video',
    DEFENSOR_CONSUMIDOR = 'defensor_consumidor',
}

export const ORBIT_FORM_DESCRIPTION =
    'Formulario completo para peticiones, quejas, reclamos y sugerencias';

export const ORBIT_CHANNEL_META: Record<
    OrbitChannelEnum,
    { title: string; description: string }
> = {
    [OrbitChannelEnum.PRINCIPAL]: {
        title: 'Solicitud PQRS',
        description: ORBIT_FORM_DESCRIPTION,
    },
    [OrbitChannelEnum.TELEFONICO]: {
        title: 'Solicitud PQRS Telefónico',
        description: ORBIT_FORM_DESCRIPTION,
    },
    [OrbitChannelEnum.WHATSAPP]: {
        title: 'Solicitud PQRS WhatsApp',
        description: ORBIT_FORM_DESCRIPTION,
    },
    [OrbitChannelEnum.BACKOFFICE]: {
        title: 'Solicitud PQRS BackOffice',
        description: ORBIT_FORM_DESCRIPTION,
    },
    [OrbitChannelEnum.CHAT]: {
        title: 'Solicitud PQRS Chat',
        description: ORBIT_FORM_DESCRIPTION,
    },
    [OrbitChannelEnum.VIDEO]: {
        title: 'Solicitud PQRS Video atención',
        description: ORBIT_FORM_DESCRIPTION,
    },
    [OrbitChannelEnum.DEFENSOR_CONSUMIDOR]: {
        title: 'Solicitud PQRS Defensor del consumidor',
        description: ORBIT_FORM_DESCRIPTION,
    },
};

export const ORBIT_CHANNEL_CONFIG: Record<
    OrbitChannelEnum,
    { embedCode: string; formId?: string }
> = {
    [OrbitChannelEnum.PRINCIPAL]: {
        embedCode: 'form_1773796380467_b4t4oj',
        formId: '56717721-c464-4af6-8234-9598a533324c',
    },
    [OrbitChannelEnum.TELEFONICO]: {
        embedCode: 'fb_mp4g5c1r_ee6744bd',
    },
    [OrbitChannelEnum.WHATSAPP]: {
        embedCode: 'fb_pqrs_whatsapp_channel',
    },
    [OrbitChannelEnum.BACKOFFICE]: {
        embedCode: 'fb_mp4g6l61_c00f4b05',
    },
    [OrbitChannelEnum.CHAT]: {
        embedCode: 'fb_pqrs_chat_channel',
    },
    [OrbitChannelEnum.VIDEO]: {
        embedCode: 'fb_pqrs_video_channel',
    },
    [OrbitChannelEnum.DEFENSOR_CONSUMIDOR]: {
        embedCode: 'fb_pqrs_defensor_consumidor_channel',
    },
};

export const ORBIT_CHANNEL_OPTIONS: ISelect[] = [
    { id: OrbitChannelEnum.TELEFONICO, name: 'Telefónico' },
    { id: OrbitChannelEnum.WHATSAPP, name: 'WhatsApp' },
    { id: OrbitChannelEnum.BACKOFFICE, name: 'BackOffice' },
    { id: OrbitChannelEnum.CHAT, name: 'Chat' },
    { id: OrbitChannelEnum.VIDEO, name: 'Video atención' },
    {
        id: OrbitChannelEnum.DEFENSOR_CONSUMIDOR,
        name: 'Defensor del consumidor',
    },
];

export const getOrbitChannelConfig = (channel?: string) =>
    channel ? ORBIT_CHANNEL_CONFIG[channel as OrbitChannelEnum] : undefined;

const DCF_HALF_NAME = 'dcf';

export const filterDcfAdmissionParams = (items: ISelect[]): ISelect[] => {
    const unique = new Map<string, ISelect>();

    items.forEach((item) => {
        const parentName = item.parent?.name?.trim().toLowerCase();
        const name = item.name?.trim().toLowerCase();

        if (parentName !== DCF_HALF_NAME || !name || unique.has(name)) return;

        unique.set(name, item);
    });

    return Array.from(unique.values());
};

const BP_CHANNEL_TO_ORBIT_CHANNEL: Record<string, OrbitChannelEnum> = {
    telefonico: OrbitChannelEnum.TELEFONICO,
    telefonia: OrbitChannelEnum.TELEFONICO,
    whatsapp: OrbitChannelEnum.WHATSAPP,
    chat: OrbitChannelEnum.CHAT,
    video: OrbitChannelEnum.VIDEO,
    'video atencion': OrbitChannelEnum.VIDEO,
    videoatencion: OrbitChannelEnum.VIDEO,
};

export const mapBpChannelToOrbitChannel = (
    rawChannel?: string
): OrbitChannelEnum | undefined => {
    if (!rawChannel) return undefined;

    const normalized = normalizeText(rawChannel);

    if (!normalized) return undefined;

    return BP_CHANNEL_TO_ORBIT_CHANNEL[normalized];
};

export const isOrbitDefensorConsumidor = (channel?: string) => {
    const value = channel?.trim().toLowerCase() ?? '';

    return (
        value === OrbitChannelEnum.DEFENSOR_CONSUMIDOR ||
        value === 'dcf' ||
        value.includes('defensor del consumidor')
    );
};

export interface OrbitChannelFormValues {
    orbitChannel: OrbitChannelEnum | '';
    sfcAdmission: string;
    correspondenceFiling: string;
}

export const initialValues: OrbitChannelFormValues = {
    orbitChannel: '',
    sfcAdmission: '',
    correspondenceFiling: '',
};

export const validationSchema = Yup.object({
    orbitChannel: Yup.string().required('El formulario es obligatorio'),
    sfcAdmission: Yup.string().when('orbitChannel', {
        is: (channel: string) => isOrbitDefensorConsumidor(channel),
        then: (schema) =>
            schema.trim().required('La clasificación es obligatoria'),
        otherwise: (schema) => schema.notRequired(),
    }),
    correspondenceFiling: Yup.string().when('orbitChannel', {
        is: (channel: string) => isOrbitDefensorConsumidor(channel),
        then: () =>
            Yup.string()
                .trim()
                .required('El radicado de correspondencia es obligatorio')
                .max(
                    200,
                    'El radicado de correspondencia debe tener 200 caracteres alfanuméricos como máximo'
                )
                .matches(
                    /^[a-zA-Z0-9\s]+$/,
                    'El radicado de correspondencia solo debe contener caracteres alfanuméricos'
                ),
    }),
});
